import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Tabs, CheckBox as Checkbox, Button } from '../../../../../../components';
import clientService from '../../../../../../api/clientService';
import { useTranslation } from 'react-i18next';
import { AxiosResponse } from 'axios';
import {
  PermissionsContainer,
  CategoryTitle,
  PermissionItem,
  PermissionDescription,
  ButtonContainer,
} from './styles';



interface Permission {
  name: string;
  display_name: string;
  granted: boolean;
  panel: string;
  description: string;
  keywords: {
    delete: string[];
    edit: string[];
  };
}

interface PanelPermissions {
  [key: string]: Permission[];
}

interface PermissionsResponse {
  user_id: string;
  permissions: {
    [key: string]: PanelPermissions;
  };
  panel_display_names: {
    [key: string]: string;
  };
}

// Categories are now dynamic from API response, no need for static mapping

const PermissionsPage: React.FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const { t } = useTranslation();
  const [permissions, setPermissions] = useState<PermissionsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<string>('');

  useEffect(() => {
    const fetchPermissions = async () => {
      if (!userId) return;
      
      try {
        const response: AxiosResponse<PermissionsResponse> = await clientService.getUserPermissions(userId);
        setPermissions(response.data);
      } catch (error) {
        console.error('Error fetching permissions:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchPermissions();
  }, [userId]);

  const handlePermissionChange = (category: string, permissionName: string, checked: boolean) => {
    if (!permissions) return;

    setPermissions(prev => {
      if (!prev?.permissions) return prev;

      const newPermissions = JSON.parse(JSON.stringify(prev));
      const availablePanels = Object.keys(newPermissions.permissions);
      const panel = availablePanels.includes('staff') ? 'staff' : availablePanels[0];

      const permission = newPermissions.permissions[panel]?.[category]?.find((p: Permission) => p.name === permissionName);
      if (permission) {
        permission.granted = checked;
      }

      return newPermissions;
    });
  };

  const handleSave = async () => {
    if (!permissions) return;

    try {
      // TODO: Implement save functionality
      console.log('Saving permissions:', permissions);
    } catch (error) {
      console.error('Error saving permissions:', error);
    }
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  if (!permissions) {
    return <div>No permissions data available</div>;
  }

  // Get the first available panel (staff, enduser, or reseller)
  const availablePanels = Object.keys(permissions.permissions || {});
  const panelKey = availablePanels.includes('staff') ? 'staff' : availablePanels[0];
  const panelPermissions = permissions.permissions?.[panelKey] || {};

  // Generate tabs dynamically from categories
  const categories = Object.keys(panelPermissions);
  const tabs = categories.map((category, index) => ({
    title: category,
    slug: category,
    id: String(index + 1)
  }));

  // Set default active tab if not set
  const currentActiveTab = activeTab || categories[0] || '';

  const getFilteredPermissions = () => {
    if (!panelPermissions || !currentActiveTab) return {};

    const categoryPermissions = panelPermissions[currentActiveTab];
    if (!categoryPermissions) return {};

    return { [currentActiveTab]: categoryPermissions };
  };

  return (
    <PermissionsContainer>
      {tabs.length > 0 && (
        <Tabs
          tabs={tabs}
          size="display"
          blockNavigate
          location={currentActiveTab}
          onChange={(tab: string) => setActiveTab(tab)}
        />
      )}

      {Object.entries(getFilteredPermissions()).map(([category, categoryPermissions]) => (
        <div key={category}>
          <CategoryTitle>{category}</CategoryTitle>
          {categoryPermissions.map((permission) => (
            <PermissionItem key={permission.name}>
              <Checkbox
                value={permission.granted}
                onChange={() => handlePermissionChange(category, permission.name, !permission.granted)}
                title=""
              />
              <PermissionDescription>
                {permission.display_name} - {permission.description}
              </PermissionDescription>
            </PermissionItem>
          ))}
        </div>
      ))}

      <ButtonContainer>
        <Button title={t('SAVE_PERMISSIONS')} onClick={handleSave} variant="primary" size="mid">
          
        </Button>
      </ButtonContainer>
    </PermissionsContainer>
  );
};

export default PermissionsPage;
